/**
 * 대용량 원문(전사 RAW, 이후 원음) 저장. 1MiB 단위로 나눠 각각 암호화한다.
 * 모든 chunk를 먼저 암호화한 뒤 한 트랜잭션으로 쓴다 → 일부 chunk만 남는 일이 없다.
 */
import { uuidv7 } from 'uuidv7';
import { open, seal, type DataKey } from './crypto/envelope';
import { utf8 } from './crypto/encoding';
import type { BlobChunk, CounselDb } from './db/schema';
import { IntegrityError, isQuotaError, NotFoundError, StorageFullError } from './errors';

export const CHUNK_BYTES = 1024 * 1024;

const blobAad = (vaultId: string, blobId: string, chunk: number, total: number) =>
  `counselai|blob|${vaultId}|${blobId}|${chunk}|${total}`;

type BlobCtx = { db: CounselDb; dataKey: DataKey; vaultId: string };

export async function putBlob(
  ctx: BlobCtx,
  data: Uint8Array | string,
  opts: { blobId?: string } = {},
): Promise<string> {
  const bytes = typeof data === 'string' ? utf8.encode(data) : data;
  const blobId = opts.blobId ?? uuidv7();
  const total = Math.max(1, Math.ceil(bytes.length / CHUNK_BYTES));
  const chunks: BlobChunk[] = [];
  for (let i = 0; i < total; i++) {
    const part = bytes.subarray(i * CHUNK_BYTES, (i + 1) * CHUNK_BYTES);
    chunks.push({
      blobId,
      chunk: i,
      total,
      env: await seal(ctx.dataKey, part, blobAad(ctx.vaultId, blobId, i, total)),
    });
  }
  try {
    await ctx.db.transaction('rw', ctx.db.blobs, async () => {
      // 멱등: 같은 id가 이미 완전하게 있으면 그대로 둔다 (불변 원문)
      const existing = await ctx.db.blobs.where('blobId').equals(blobId).count();
      if (existing > 0) return;
      await ctx.db.blobs.bulkPut(chunks);
    });
  } catch (e) {
    if (isQuotaError(e)) throw new StorageFullError();
    throw e;
  }
  return blobId;
}

export async function getBlobBytes(ctx: BlobCtx, blobId: string): Promise<Uint8Array> {
  const rows = (await ctx.db.blobs.where('blobId').equals(blobId).toArray()).sort((a, b) => a.chunk - b.chunk);
  if (rows.length === 0) throw new NotFoundError('Blob', blobId);
  const total = rows[0]!.total;
  if (rows.length !== total || rows.some((r, i) => r.chunk !== i || r.total !== total)) {
    throw new IntegrityError('원문 일부가 누락되었습니다');
  }
  const parts = await Promise.all(
    rows.map((r) => open(ctx.dataKey, r.env, blobAad(ctx.vaultId, blobId, r.chunk, total))),
  );
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

export async function getBlobText(ctx: BlobCtx, blobId: string): Promise<string> {
  return utf8.decode(await getBlobBytes(ctx, blobId));
}
